import { useState, useRef, useMemo, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import toast from "react-hot-toast";

import {
    useAdminGalleries,
    useCreateGallery,
    useUpdateGallery,
    useDeleteGallery,
} from "@/services/adminGalleryService";
import type { AdminGalleryItem, GalleryImage } from "@/services/adminGalleryService";

import { Button } from "@/components/ui/button";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Edit2, Plus, Trash2, Images, Loader2, ImagePlus, Search, X, ImageIcon, Link2, Code2 } from "lucide-react";

const formSchema = z.object({
    title: z.string().optional(),
    album: z.string().optional(),
    description: z.string().optional(),
    display_order: z.coerce.number().min(0).default(0),
    is_active: z.boolean().default(true),
});
type FormValues = z.infer<typeof formSchema>;

export default function GalleryPage() {
    const { data: galleries, isLoading } = useAdminGalleries();

    const createMutation = useCreateGallery();
    const updateMutation = useUpdateGallery();
    const deleteMutation = useDeleteGallery();

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingGallery, setEditingGallery] = useState<AdminGalleryItem | null>(null);

    const [isAlertOpen, setIsAlertOpen] = useState(false);
    const [alertConfig, setAlertConfig] = useState<{
        title: string;
        description: string;
        actionText: string;
        action: () => void;
        isDestructive?: boolean;
    } | null>(null);

    // Foto baru yang dipilih user (dipakai untuk create, atau ditambahkan saat edit)
    const [newImageFiles, setNewImageFiles] = useState<File[]>([]);
    const [newImagePreviews, setNewImagePreviews] = useState<string[]>([]);
    // URL gambar eksternal & embed iframe baru yang ditambahkan user
    const [newImageUrls, setNewImageUrls] = useState<string[]>([]);
    const [newEmbedUrls, setNewEmbedUrls] = useState<string[]>([]);
    const [imageUrlInput, setImageUrlInput] = useState("");
    const [embedUrlInput, setEmbedUrlInput] = useState("");
    // Foto yang sudah ada di server (hanya terisi saat edit) - bisa ditandai utk dihapus
    const [existingImages, setExistingImages] = useState<GalleryImage[]>([]);
    const [deleteImageIds, setDeleteImageIds] = useState<number[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Terima URL polos atau kode <iframe src="...">, ambil URL-nya saja
    const extractEmbedUrl = (raw: string) => {
        const match = raw.match(/src=["']([^"']+)["']/i);
        return (match ? match[1] : raw).trim();
    };

    const handleAddImageUrl = () => {
        const url = imageUrlInput.trim();
        if (!url) return;
        setNewImageUrls((prev) => [...prev, url]);
        setImageUrlInput("");
    };

    const handleAddEmbedUrl = () => {
        const url = extractEmbedUrl(embedUrlInput);
        if (!url) return;
        setNewEmbedUrls((prev) => [...prev, url]);
        setEmbedUrlInput("");
    };

    const handleRemoveNewImageUrl = (idx: number) => {
        setNewImageUrls((prev) => prev.filter((_, i) => i !== idx));
    };

    const handleRemoveNewEmbedUrl = (idx: number) => {
        setNewEmbedUrls((prev) => prev.filter((_, i) => i !== idx));
    };

    const [search, setSearch] = useState("");
    const [albumFilter, setAlbumFilter] = useState("all");
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(12);

    const albumOptions = useMemo(() => {
        const unique = new Set<string>();
        (galleries || []).forEach((g) => { if (g.album) unique.add(g.album); });
        return Array.from(unique).sort();
    }, [galleries]);

    const filteredGalleries = (galleries || []).filter((g) =>
        ((g.title || "").toLowerCase().includes(search.toLowerCase()) ||
            (g.album || "").toLowerCase().includes(search.toLowerCase())) &&
        (albumFilter === "all" || g.album === albumFilter)
    );

    const totalItems = filteredGalleries.length;
    const totalPages = Math.ceil(totalItems / perPage) || 1;
    const currentPage = Math.min(page, totalPages);
    const paginatedGalleries = filteredGalleries.slice((currentPage - 1) * perPage, currentPage * perPage);
    const fromItem = totalItems === 0 ? 0 : (currentPage - 1) * perPage + 1;
    const toItem = Math.min(currentPage * perPage, totalItems);

    useEffect(() => {
        setPage(1);
    }, [search, albumFilter]);

    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            title: "",
            album: "",
            display_order: 0,
            is_active: true,
        },
    });

    const handleOpenDialog = (gallery?: AdminGalleryItem) => {
        newImagePreviews.forEach((url) => URL.revokeObjectURL(url));
        setNewImageFiles([]);
        setNewImagePreviews([]);
        setNewImageUrls([]);
        setNewEmbedUrls([]);
        setImageUrlInput("");
        setEmbedUrlInput("");
        setDeleteImageIds([]);
        if (fileInputRef.current) fileInputRef.current.value = "";

        if (gallery) {
            setEditingGallery(gallery);
            setExistingImages(gallery.images || []);
            form.reset({
                title: gallery.title || "",
                album: gallery.album || "",
                description: gallery.description || "",
                display_order: gallery.display_order,
                is_active: Boolean(gallery.is_active),
            });
        } else {
            setEditingGallery(null);
            setExistingImages([]);
            form.reset({
                title: "",
                album: "",
                description: "",
                display_order: 0,
                is_active: true,
            });
        }
        setIsDialogOpen(true);
    };

    const handleAddFiles = (files: FileList | null) => {
        if (!files || files.length === 0) return;
        const arr = Array.from(files);
        setNewImageFiles((prev) => [...prev, ...arr]);
        setNewImagePreviews((prev) => [...prev, ...arr.map((f) => URL.createObjectURL(f))]);
    };

    const handleRemoveNewFile = (idx: number) => {
        URL.revokeObjectURL(newImagePreviews[idx]);
        setNewImageFiles((prev) => prev.filter((_, i) => i !== idx));
        setNewImagePreviews((prev) => prev.filter((_, i) => i !== idx));
    };

    const handleToggleDeleteExisting = (id: number) => {
        setDeleteImageIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    };

    const remainingExistingCount = existingImages.filter((img) => !deleteImageIds.includes(img.id)).length;
    const totalPhotoCount = remainingExistingCount + newImageFiles.length + newImageUrls.length + newEmbedUrls.length;

    const onSubmit = (values: FormValues) => {
        if (totalPhotoCount === 0) {
            toast.error("Minimal 1 foto wajib diunggah");
            return;
        }

        const formData = new FormData();
        formData.append("title", values.title || "");
        formData.append("album", values.album || "");
        formData.append("description", values.description || "");
        formData.append("display_order", values.display_order.toString());
        formData.append("is_active", values.is_active ? "1" : "0");

        if (editingGallery) {
            newImageFiles.forEach((file) => formData.append("new_images[]", file));
            newImageUrls.forEach((url) => formData.append("new_image_urls[]", url));
            newEmbedUrls.forEach((url) => formData.append("new_embed_urls[]", url));
            deleteImageIds.forEach((id) => formData.append("delete_image_ids[]", String(id)));

            updateMutation.mutate(
                { id: editingGallery.id, payload: formData },
                {
                    onSuccess: () => {
                        toast.success("Foto berhasil diperbarui");
                        setIsDialogOpen(false);
                    },
                    onError: (err: any) => {
                        toast.error(err.response?.data?.message || "Gagal memperbarui foto");
                    },
                }
            );
        } else {
            newImageFiles.forEach((file) => formData.append("images[]", file));
            newImageUrls.forEach((url) => formData.append("image_urls[]", url));
            newEmbedUrls.forEach((url) => formData.append("embed_urls[]", url));

            createMutation.mutate(formData, {
                onSuccess: () => {
                    toast.success("Foto berhasil ditambahkan");
                    setIsDialogOpen(false);
                },
                onError: (err: any) => {
                    toast.error(err.response?.data?.message || "Gagal menambahkan foto");
                },
            });
        }
    };

    const handleDelete = (id: number) => {
        setAlertConfig({
            title: "Hapus Foto",
            description: "Apakah Anda yakin ingin menghapus foto ini? Tindakan ini tidak dapat dibatalkan.",
            actionText: "Hapus",
            isDestructive: true,
            action: () => {
                deleteMutation.mutate(id, {
                    onSuccess: () => toast.success("Foto berhasil dihapus"),
                    onError: (error: any) => toast.error(error?.response?.data?.message || "Gagal menghapus foto"),
                });
            },
        });
        setIsAlertOpen(true);
    };

    const isMutating = createMutation.isPending || updateMutation.isPending;

    return (
        <div className="max-w-7xl mx-auto space-y-8 pb-20 px-4 md:px-0">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 bg-white dark:bg-slate-900 p-6 md:p-8 rounded-3xl shadow-sm border border-slate-100 dark:border-slate-800">
                <div>
                    <div className="flex items-center gap-3 mb-2">
                        <div className="p-2.5 rounded-xl bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400">
                            <Images className="w-6 h-6" />
                        </div>
                        <h1 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">Kelola Galeri</h1>
                    </div>
                    <p className="text-sm text-slate-500 dark:text-slate-400 max-w-xl">
                        Kelola foto dokumentasi kegiatan Konaspi yang tampil di halaman publik.
                    </p>
                </div>
                <div className="flex items-center gap-3 shrink-0 flex-wrap">
                    <Button onClick={() => handleOpenDialog()} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl h-11 px-6 font-bold shadow-md transition-all">
                        <Plus className="w-4 h-4 mr-2" /> Tambah Foto
                    </Button>
                </div>
            </div>

            <div className="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col sm:flex-row gap-4 items-center">
                <div className="relative flex-1 w-full">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                        type="text"
                        placeholder="Cari judul atau album..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full h-12 pl-12 pr-4 py-2 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none transition-all font-medium"
                    />
                </div>
                {albumOptions.length > 0 && (
                    <div className="w-full sm:w-64 shrink-0">
                        <select
                            value={albumFilter}
                            onChange={(e) => setAlbumFilter(e.target.value)}
                            className="w-full h-12 px-4 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none transition-all font-medium"
                        >
                            <option value="all">Semua Album</option>
                            {albumOptions.map((album) => (
                                <option key={album} value={album}>{album}</option>
                            ))}
                        </select>
                    </div>
                )}
            </div>

            {isLoading ? (
                <div className="flex justify-center items-center py-20">
                    <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
                </div>
            ) : totalItems > 0 ? (
              <>
                <div className="flex flex-col gap-4">
                    {paginatedGalleries.map((item) => {
                        const hasMultiple = item.images && item.images.length > 1;
                        return (
                            <div key={item.id} className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-sm hover:shadow-md transition-shadow group flex flex-col sm:flex-row">
                                <div className="relative w-full sm:w-72 h-48 sm:h-auto shrink-0 p-4 sm:p-5">
                                    <div className="relative w-full h-full min-h-[160px]">
                                        {hasMultiple && (
                                            <>
                                                <div className="absolute inset-0 bg-slate-200 dark:bg-slate-700 rounded-xl transform rotate-3 scale-95 opacity-50 z-0 border border-white/50 transition-transform group-hover:rotate-6"></div>
                                                <div className="absolute inset-0 bg-slate-300 dark:bg-slate-600 rounded-xl transform -rotate-2 scale-95 opacity-70 z-0 border border-white/50 mt-1 ml-1 transition-transform group-hover:-rotate-4"></div>
                                            </>
                                        )}
                                        <div className="absolute inset-0 z-10 rounded-xl overflow-hidden bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-sm">
                                            {item.images?.[0]?.type === "embed" ? (
                                                <div className="w-full h-full flex flex-col items-center justify-center text-slate-400 gap-2 bg-slate-50 dark:bg-slate-800/50">
                                                    <Code2 className="w-10 h-10" />
                                                    <span className="text-xs font-medium">Embed Content</span>
                                                </div>
                                            ) : (item.images?.[0]?.image_url || item.image_url) ? (
                                                <img src={item.images?.[0]?.image_url || item.image_url || ""} alt={item.title || ""} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center text-slate-300 bg-slate-50 dark:bg-slate-800/50">
                                                    <ImageIcon className="w-12 h-12" />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    {!item.is_active && (
                                        <span className="absolute top-6 left-6 z-20 inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold bg-rose-500/90 text-white backdrop-blur-sm shadow-sm">Nonaktif</span>
                                    )}
                                    {hasMultiple && (
                                        <span className="absolute top-6 right-6 z-20 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-white/95 dark:bg-slate-900/95 text-slate-800 dark:text-slate-100 backdrop-blur-sm shadow-sm">
                                            <Images className="w-3.5 h-3.5 text-indigo-500" /> {item.images.length} Foto
                                        </span>
                                    )}
                                </div>
                                <div className="p-4 sm:p-5 sm:pl-0 flex flex-col flex-1">
                                    <div className="flex justify-between items-start gap-4">
                                        <div className="space-y-1">
                                            <h3 className="font-bold text-lg md:text-xl text-slate-900 dark:text-white line-clamp-1 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                                                {item.title || "(Tanpa judul)"}
                                            </h3>
                                            <div className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-md bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400">
                                                {item.album || "Tanpa Album"}
                                            </div>
                                        </div>
                                        <div className="flex shrink-0 gap-2">
                                            <Button
                                                variant="outline"
                                                size="icon"
                                                className="h-9 w-9 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-xl border-slate-200 dark:border-slate-700"
                                                onClick={() => handleOpenDialog(item)}
                                            >
                                                <Edit2 className="w-4 h-4" />
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="icon"
                                                className="h-9 w-9 text-rose-600 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-xl border-slate-200 dark:border-slate-700"
                                                onClick={() => handleDelete(item.id)}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </div>
                                    
                                    {item.description ? (
                                        <div className="text-sm text-slate-600 dark:text-slate-400 mt-3 line-clamp-2 md:line-clamp-3 leading-relaxed">
                                            {item.description}
                                        </div>
                                    ) : (
                                        <div className="text-sm text-slate-400 dark:text-slate-500 mt-3 italic">
                                            Tidak ada deskripsi.
                                        </div>
                                    )}
                                    
                                    <div className="mt-auto pt-4 flex flex-wrap items-center justify-between gap-4 border-t border-slate-100 dark:border-slate-800/60 mt-4">
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs font-medium text-slate-500">Urutan Tampil:</span>
                                            <span className="text-xs font-bold text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-md">{item.display_order}</span>
                                        </div>
                                        
                                        {hasMultiple && (
                                            <div className="flex items-center gap-2">
                                                <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mr-1">Preview</span>
                                                <div className="flex -space-x-2 overflow-hidden">
                                                    {item.images.slice(0, 5).map((img) => (
                                                        <div key={img.id} className="inline-block h-8 w-8 rounded-full ring-2 ring-white dark:ring-slate-900 bg-slate-200 overflow-hidden">
                                                            {img.type === 'embed' ? (
                                                                <div className="w-full h-full flex items-center justify-center bg-slate-100 dark:bg-slate-800">
                                                                    <Code2 className="w-4 h-4 text-slate-400" />
                                                                </div>
                                                            ) : (
                                                                <img src={img.image_url || ""} className="w-full h-full object-cover" alt="thumbnail" />
                                                            )}
                                                        </div>
                                                    ))}
                                                    {item.images.length > 5 && (
                                                        <div className="inline-flex h-8 w-8 rounded-full ring-2 ring-white dark:ring-slate-900 bg-slate-100 dark:bg-slate-800 items-center justify-center text-[10px] font-bold text-slate-600 dark:text-slate-300">
                                                            +{item.images.length - 5}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-4 md:p-5 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
                    <div className="flex items-center gap-3 text-slate-500 font-medium">
                        <span>Menampilkan {fromItem} - {toItem} dari {totalItems} data</span>
                        <div className="h-4 w-px bg-slate-300 dark:bg-slate-700 hidden sm:block"></div>
                        <div className="flex items-center gap-2">
                            <label htmlFor="perPage">Tampilkan:</label>
                            <select
                                id="perPage"
                                value={perPage}
                                onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
                                className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md px-2 py-1 outline-none focus:ring-2 focus:ring-indigo-500 font-bold"
                            >
                                <option value="12">12</option>
                                <option value="24">24</option>
                                <option value="48">48</option>
                                <option value="96">96</option>
                            </select>
                        </div>
                    </div>

                    {totalPages > 1 && (
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                                className="rounded-lg"
                            >
                                Sebelumnya
                            </Button>
                            <span className="text-slate-500 font-medium px-1">
                                Hal {currentPage} / {totalPages}
                            </span>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                                className="rounded-lg"
                            >
                                Selanjutnya
                            </Button>
                        </div>
                    )}
                </div>
              </>
            ) : (
                <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-12 text-center text-slate-500">
                    Belum ada data foto galeri
                </div>
            )}

            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editingGallery ? "Edit Foto" : "Tambah Foto Baru"}</DialogTitle>
                    </DialogHeader>

                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 pt-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <FormField
                                    control={form.control}
                                    name="title"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Judul</FormLabel>
                                            <FormControl>
                                                <Input placeholder="Contoh: Sesi Keynote" {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="album"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Album / Kategori</FormLabel>
                                            <FormControl>
                                                <Input placeholder="Contoh: Keynote" {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>

                            <FormField
                                control={form.control}
                                name="description"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Deskripsi</FormLabel>
                                        <FormControl>
                                            <Textarea placeholder="Deskripsi singkat foto/album ini..." rows={3} {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="display_order"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Urutan Tampil</FormLabel>
                                        <FormControl>
                                            <Input type="number" min="0" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="is_active"
                                render={({ field }) => (
                                    <FormItem className="flex flex-row items-center justify-between border p-4 rounded-xl shadow-sm">
                                        <div className="space-y-0.5">
                                            <FormLabel className="text-sm font-semibold">Tampilkan di Halaman Publik?</FormLabel>
                                            <div className="text-xs text-muted-foreground">Nonaktifkan untuk menyembunyikan tanpa menghapus.</div>
                                        </div>
                                        <FormControl>
                                            <Switch checked={field.value} onCheckedChange={field.onChange} />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />

                            <FormItem>
                                <FormLabel>
                                    Foto / URL / Embed {totalPhotoCount === 0 && <span className="text-red-500">*</span>}
                                    <span className="text-xs text-slate-400 font-normal ml-2">({totalPhotoCount} item)</span>
                                </FormLabel>
                                <FormControl>
                                    <div className="flex flex-col gap-4">
                                        {(existingImages.length > 0 || newImagePreviews.length > 0 || newImageUrls.length > 0 || newEmbedUrls.length > 0) && (
                                            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                                                {existingImages.map((img) => {
                                                    const marked = deleteImageIds.includes(img.id);
                                                    return (
                                                        <div key={img.id} className="relative group">
                                                            {img.type === "embed" ? (
                                                                <div className={`w-full h-24 flex flex-col items-center justify-center gap-1 rounded-lg border bg-slate-50 dark:bg-slate-800 text-slate-400 ${marked ? "border-rose-400 opacity-40" : "border-slate-200 dark:border-slate-700"}`}>
                                                                    <Code2 className="w-6 h-6" />
                                                                    <span className="text-[9px] font-medium px-1 truncate w-full text-center">{img.embed_url}</span>
                                                                </div>
                                                            ) : (
                                                                <img
                                                                    src={img.image_url || ""}
                                                                    alt=""
                                                                    className={`w-full h-24 object-cover rounded-lg border ${marked ? "border-rose-400 opacity-40" : "border-slate-200 dark:border-slate-700"}`}
                                                                />
                                                            )}
                                                            <button
                                                                type="button"
                                                                onClick={() => handleToggleDeleteExisting(img.id)}
                                                                title={marked ? "Batal hapus" : "Tandai untuk dihapus"}
                                                                className={`absolute top-1 right-1 w-6 h-6 rounded-full flex items-center justify-center text-white shadow ${marked ? "bg-slate-500" : "bg-rose-500 hover:bg-rose-600"}`}
                                                            >
                                                                <X className="w-3.5 h-3.5" />
                                                            </button>
                                                            {marked && (
                                                                <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-rose-600 bg-white/70 dark:bg-slate-900/70 rounded-lg">
                                                                    Akan dihapus
                                                                </span>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                                {newImagePreviews.map((url, idx) => (
                                                    <div key={url} className="relative group">
                                                        <img src={url} alt="" className="w-full h-24 object-cover rounded-lg border border-indigo-300 dark:border-indigo-700" />
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemoveNewFile(idx)}
                                                            title="Hapus foto ini"
                                                            className="absolute top-1 right-1 w-6 h-6 rounded-full flex items-center justify-center text-white bg-rose-500 hover:bg-rose-600 shadow"
                                                        >
                                                            <X className="w-3.5 h-3.5" />
                                                        </button>
                                                        <span className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded text-[9px] font-bold bg-indigo-600 text-white">Baru</span>
                                                    </div>
                                                ))}
                                                {newImageUrls.map((url, idx) => (
                                                    <div key={`imgurl-${idx}`} className="relative group">
                                                        <img src={url} alt="" className="w-full h-24 object-cover rounded-lg border border-indigo-300 dark:border-indigo-700" onError={(e) => { (e.target as HTMLImageElement).style.opacity = "0.3"; }} />
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemoveNewImageUrl(idx)}
                                                            title="Hapus URL ini"
                                                            className="absolute top-1 right-1 w-6 h-6 rounded-full flex items-center justify-center text-white bg-rose-500 hover:bg-rose-600 shadow"
                                                        >
                                                            <X className="w-3.5 h-3.5" />
                                                        </button>
                                                        <span className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded text-[9px] font-bold bg-sky-600 text-white">URL</span>
                                                    </div>
                                                ))}
                                                {newEmbedUrls.map((url, idx) => (
                                                    <div key={`embedurl-${idx}`} className="relative group">
                                                        <div className="w-full h-24 flex flex-col items-center justify-center gap-1 rounded-lg border border-amber-300 dark:border-amber-700 bg-slate-50 dark:bg-slate-800 text-slate-400">
                                                            <Code2 className="w-6 h-6" />
                                                            <span className="text-[9px] font-medium px-1 truncate w-full text-center">{url}</span>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemoveNewEmbedUrl(idx)}
                                                            title="Hapus embed ini"
                                                            className="absolute top-1 right-1 w-6 h-6 rounded-full flex items-center justify-center text-white bg-rose-500 hover:bg-rose-600 shadow"
                                                        >
                                                            <X className="w-3.5 h-3.5" />
                                                        </button>
                                                        <span className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-600 text-white">Embed</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        <Button
                                            type="button"
                                            variant="outline"
                                            className="w-full flex items-center gap-2 border-dashed border-2 py-8 text-gray-500 hover:text-primary hover:border-primary"
                                            onClick={() => fileInputRef.current?.click()}
                                        >
                                            <ImagePlus className="w-6 h-6" />
                                            Klik untuk tambah foto (JPG, PNG, WEBP - bisa pilih lebih dari 1)
                                        </Button>
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            className="hidden"
                                            accept="image/*"
                                            multiple
                                            onChange={(e) => {
                                                handleAddFiles(e.target.files);
                                                e.target.value = "";
                                            }}
                                        />

                                        <div className="flex flex-col gap-4">
                                            <div className="flex flex-col gap-2">
                                                <label className="text-xs font-semibold text-slate-600 dark:text-slate-300 flex items-center gap-1.5">
                                                    <Link2 className="w-3.5 h-3.5" /> URL Gambar Eksternal
                                                </label>
                                                <div className="flex gap-2">
                                                    <input
                                                        type="text"
                                                        value={imageUrlInput}
                                                        onChange={(e) => setImageUrlInput(e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === "Enter") {
                                                                e.preventDefault();
                                                                handleAddImageUrl();
                                                            }
                                                        }}
                                                        placeholder="https://contoh.com/gambar.jpg"
                                                        className="w-full h-11 px-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                                                    />
                                                    <Button type="button" variant="outline" onClick={handleAddImageUrl} className="h-11 px-5 rounded-xl shrink-0">
                                                        Tambah
                                                    </Button>
                                                </div>
                                            </div>
                                            <div className="flex flex-col gap-2">
                                                <label className="text-xs font-semibold text-slate-600 dark:text-slate-300 flex items-center gap-1.5">
                                                    <Code2 className="w-3.5 h-3.5" /> URL / Kode Embed Iframe
                                                </label>
                                                <Textarea
                                                    value={embedUrlInput}
                                                    onChange={(e) => setEmbedUrlInput(e.target.value)}
                                                    placeholder={'Tempel URL langsung, atau kode lengkap <iframe src="...">...</iframe> (YouTube, Google Drive, dll)'}
                                                    rows={3}
                                                    className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm focus-visible:ring-2 focus-visible:ring-indigo-500 resize-y"
                                                />
                                                <Button type="button" variant="outline" onClick={handleAddEmbedUrl} className="h-11 px-5 rounded-xl self-end">
                                                    Tambah Embed
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </FormControl>
                            </FormItem>

                            <div className="flex gap-3 pt-4 justify-end">
                                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="rounded-xl h-11 px-6 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800">
                                    Batal
                                </Button>
                                <Button type="submit" disabled={isMutating} className="rounded-xl h-11 px-6 bg-indigo-600 hover:bg-indigo-700 text-white font-bold shadow-md">
                                    {isMutating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                    Simpan
                                </Button>
                            </div>
                        </form>
                    </Form>
                </DialogContent>
            </Dialog>

            <AlertDialog open={isAlertOpen} onOpenChange={setIsAlertOpen}>
                <AlertDialogContent className="rounded-3xl border-slate-200 dark:border-slate-800 p-6 shadow-2xl max-w-sm">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-xl font-bold text-slate-900 dark:text-white">
                            {alertConfig?.title}
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-500 dark:text-slate-400">
                            {alertConfig?.description}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="mt-6 gap-2">
                        <AlertDialogCancel className="rounded-xl h-11 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 font-medium">Batal</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => {
                                alertConfig?.action();
                                setIsAlertOpen(false);
                            }}
                            className={`rounded-xl h-11 font-bold text-white shadow-md ${
                                alertConfig?.isDestructive
                                    ? "bg-rose-600 hover:bg-rose-700"
                                    : "bg-indigo-600 hover:bg-indigo-700"
                            }`}
                        >
                            {alertConfig?.actionText}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
